import axios, { AxiosResponse } from "axios";
import data from "./config.json";
import {
  IUserResetPassword,
  IUserFormValues,
  IUser,
  IUserChangePassword,
  IUsers,
  IChangeState,
  IUserCompany,
  IUserAdmin,
} from "../models/user";
import { IBroker, INotificationSignature } from "../models/company";
import { IChangeBrokerState } from "../models/broker";
import { IChangeClientState, IClientList, IClientForm } from "../models/client";
import { ISellerForm, IChangeSellerState } from "../models/seller";
import { IPackageBroker, IPackageForm } from "../models/package";
import { ISalesClient } from "../models/dashboard";
import {
  IBrokerAutocomplete,
  IDocumentReportBrokerResult,
  IDocumentReportSudoResult,
  IDocumentReportTenantResult,
  ITenantAutocomplete,
} from "../models/report";

let apiSecurity = data.find((dat) => dat.name === "Security")?.path;
let apiReport = data.find((dat) => dat.name === "Report")?.path;
let apiGeneral = data.find((dat) => dat.name === "General")?.path;

//variables aux para el manejo de tokens
let auxTokenGeneral: string;
let bTokenGeneral: boolean = false;

axios.interceptors.request.use(
  (config) => {
    if (!bTokenGeneral) {
      const token = window.localStorage.getItem("jwtGeneral");
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    } else {
      config.headers.Authorization = `Bearer ${auxTokenGeneral}`;
      bTokenGeneral = false;
      return config;
    }
  },
  (error) => {
    return Promise.reject(error);
  }
);

const responseBody = (response: AxiosResponse) => response.data;

const requests = {
  get: (url: string) => axios.get(url).then(responseBody),
  getWithBody: (url: string, body: {}) =>
    axios.get(url, { params: body }).then(responseBody),
  post: (url: string, body: {}) => axios.post(url, body).then(responseBody),
  put: (url: string, body: {}) => axios.put(url, body).then(responseBody),
  patch: (url: string, body: {}) => axios.patch(url, body).then(responseBody),
  del: (url: string) => axios.delete(url).then(responseBody),
  postForm: (url: string, file: Blob) => {
    let formData = new FormData();
    formData.append("File", file);
    return axios
      .post(url, formData, {
        headers: { "Content-type": "multipart/form-data" },
      })
      .then(responseBody);
  },
};

const User = {
  resetPassword: (
    resetPassword: IUserResetPassword,
    token: string
  ): Promise<boolean> => {
    bTokenGeneral = true;
    auxTokenGeneral = token;
    return axios.post(
      `${apiSecurity}/Authentication/ResetPassword`,
      resetPassword,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
  },
  login: (user: IUserFormValues): Promise<IUser> =>
    requests.post(`${apiSecurity}/Authentication/Market/Authenticate`, user),
  changePassword: (paaswordCange: IUserChangePassword): Promise<boolean> =>
    requests.post(
      `${apiSecurity}/Authentication/ChangePassword`,
      paaswordCange
    ),
  sendReset: (username: string): Promise<string> =>
    requests.get(`${apiSecurity}/Authentication/SendReset/${username}`),
  editUser: (id: string, userEdit: any): Promise<IUser> =>
    requests.patch(`${apiSecurity}/User/${id}`, userEdit),
  relogUser: (): Promise<IUser> => requests.get(`${apiSecurity}/User/UserInfo`),
};

//usuarios de mi empresa
const UserCompany = {
  getUsers: (): Promise<IUsers[]> => requests.get(`${apiSecurity}/User/Broker`),
  editUser: (id: string, userEdit: any): Promise<any> =>
    requests.patch(`${apiSecurity}/User/${id}/Broker`, userEdit),
  createUser: (user: IUserCompany): Promise<any> =>
    requests.post(`${apiSecurity}/User/Broker`, user),
  getCheckRegister: (username: string): Promise<boolean> =>
    requests.get(`${apiSecurity}/User/CheckRegister/${username}`),
  changeUserState: (userChange: IChangeState): Promise<boolean> =>
    requests.patch(`${apiSecurity}/User/ChangeState`, userChange),
};

//agentes comerciales
const Broker = {
  getBrokers: (): Promise<any[]> => requests.get(`${apiGeneral}/Broker`),
  updateBroker: (broker: any): Promise<IBroker> =>
    requests.patch(`${apiGeneral}/Broker/basicData`, broker),
  newBroker: (broker: any): Promise<IBroker> =>
    requests.post(`${apiGeneral}/Broker`, broker),
  getBrokerById: (id: string): Promise<IBroker> =>
    requests.get(`${apiGeneral}/Broker/${id}`),
  changeBrokerState: (state: IChangeBrokerState): Promise<boolean> =>
    requests.patch(`${apiGeneral}/Broker/ChangeIsActive`, state),
  getUsersByBrokerId: (id: string): Promise<IUsers[]> =>
    requests.get(`${apiSecurity}/User/Broker/${id}`),
  createUserByBrokerId: (id: string, user: IUserCompany): Promise<any> =>
    requests.post(`${apiSecurity}/User/Broker/${id}`, user),
  editUserBroker: (
    id: string,
    user: IUserCompany,
    brokerId: string
  ): Promise<any> =>
    requests.patch(`${apiSecurity}/User/${id}/Broker/${brokerId}`, user),
  getPackageBrokers: (id: string): Promise<IPackageBroker[]> =>
    requests.get(`${apiGeneral}/Price/Broker/${id}`),
  postPackageBrokers: (values: any, id: string): Promise<IPackageBroker[]> =>
    requests.post(`${apiGeneral}/Price/many/broker/${id}`, values),
};
const UsersAdminBroker = {
  getUsersBroker: (id: string): Promise<IUsers[]> =>
    requests.get(`${apiSecurity}/User/Broker`),
  editUserBroker: (id: string, userEdit: IUserCompany): Promise<any> =>
    requests.patch(`${apiSecurity}/User/Broker/${id}`, userEdit),
  createUserBroker: (user: IUserCompany): Promise<any> =>
    requests.post(`${apiSecurity}/User/Broker`, user),
};

const Clients = {
  getClients: (): Promise<IClientList[]> =>
    requests.get(`${apiGeneral}/Supplier/all`),
  changeClientState: (state: IChangeClientState): Promise<boolean> =>
    requests.patch(`${apiGeneral}/Supplier/ChangeIsActive`, state),
  getClientById: (id: string): Promise<any> =>
    requests.get(`${apiGeneral}/Supplier/${id}`),
  updateClient: (client: IClientForm): Promise<IClientList> =>
    requests.patch(`${apiGeneral}/Supplier/update`, client),
  newClient: (client: IClientForm): Promise<IClientList> =>
    requests.post(`${apiGeneral}/Supplier/create`, client),
  getUsersByClientId: (id: string): Promise<IUsers[]> =>
    requests.get(`${apiSecurity}/User/Tenant/${id}`),
  newUserClient: (id: string, user: IUserAdmin): Promise<any> =>
    requests.post(`${apiSecurity}/User/Tenant/${id}`, user),
  editUserClient: (id: string, user: any): Promise<any> =>
    requests.patch(`${apiSecurity}/User/${id}/Tenant`, user),
};

const ClientsDashboard = {
  getFirstYearOfBilling: (id: string): Promise<number> =>
    requests.get(`${apiGeneral}/Report/FirstYearOfBilling/Tenant/${id}`),
  getGraphDataYearSuccessDocuments: (
    year: number,
    id: string
  ): Promise<ISalesClient[]> =>
    requests.get(
      `${apiGeneral}/Report/GraphDataYearSuccessDocuments/${year}/Tenant/${id}`
    ),
};

const ListOfValours = {
  getCities: (stateCode: string): Promise<any[]> =>
    requests.get(`${apiReport}/ListOfValues/SubStates/${stateCode}`),
  getCountrySubentities: (): Promise<any[]> =>
    requests.get(`${apiReport}/ListOfValues/States`),
  getRejectionConcepts: (): Promise<any[]> =>
    requests.get(`${apiReport}/ListOfValues/RejectionConcepts`),
};

const Company = {
  getBroker: (token: string): Promise<any> => {
    bTokenGeneral = true;
    auxTokenGeneral = token;
    return axios.get(`${apiGeneral}/Broker/byToken`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },
  uploadLogo: (file: Blob): Promise<IBroker> =>
    requests.postForm(`${apiGeneral}/Broker/logo`, file),
  patchSignature: (signature: INotificationSignature): Promise<boolean> =>
    requests.patch(`${apiGeneral}/Broker/Signature`, signature),
};

const Seller = {
  getSellers: (): Promise<ISellerForm[]> => requests.get(`${apiGeneral}/Seller`),
  newSellert: (seller: ISellerForm): Promise<ISellerForm> =>
    requests.post(`${apiGeneral}/Seller`, seller),
  editSellert: (seller: ISellerForm): Promise<ISellerForm> =>
    requests.patch(`${apiGeneral}/Seller`, seller),
  getSellerById: (id: string): Promise<ISellerForm> =>
    requests.get(`${apiGeneral}/Seller/${id}`),
  changeSellerState: (state: IChangeSellerState): Promise<boolean> =>
    requests.patch(`${apiGeneral}/Seller/ChangeIsActive`, state),
};

const Package = {
  getPackages: (id: string): Promise<IPackageForm[]> =>
    requests.get(`${apiGeneral}/Package/Tenant/${id}`),
  newPackage: (packageForm: IPackageForm): Promise<IPackageForm> =>
    requests.post(`${apiGeneral}/Package`, packageForm),
  editPackage: (packageForm: IPackageForm): Promise<IPackageForm> =>
    requests.put(`${apiGeneral}/Package/${packageForm.id}`, packageForm),
};

const Report = {
  getDocumentReportSudoByFilter: (
    filter: any
  ): Promise<IDocumentReportSudoResult[]> =>
    requests.getWithBody(
      `${apiGeneral}/Report/DocumentReportSudoByFilter`,
      filter
    ),
  getBrokersAutocomplete: (search: string): Promise<IBrokerAutocomplete[]> =>
    requests.get(`${apiGeneral}/Report/broker/${search}`),

  getDocumentReportBrokerByFilter: (
    filter: any
  ): Promise<IDocumentReportBrokerResult[]> =>
    requests.getWithBody(
      `${apiGeneral}/Report/DocumentReportBrokerByFilter`,
      filter
    ),

  getTenantsAutocomplete: (search: string): Promise<ITenantAutocomplete[]> =>
    requests.get(`${apiGeneral}/Report/tenant/${search}`),

  getDocumentReportTenantByFilter: (
    filter: any
  ): Promise<IDocumentReportTenantResult[]> =>
    requests.getWithBody(
      `${apiGeneral}/Report/DocumentReportTenantByFilter`,
      filter
    ),

  getDetailedReportTenantByRangeDate: (body): Promise<any> => requests.post(`${apiGeneral}/Report/DocumentReportTenantByFilter/Export`, body),
};

export default {
  User,
  ListOfValours,
  Company,
  UserCompany,
  Broker,
  UsersAdminBroker,
  Clients,
  Seller,
  Package,
  ClientsDashboard,
  Report,
};
